import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle, Download, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { waLink, quoteMessage } from "@/lib/whatsapp";
import TankPlaceholder from "@/components/TankPlaceholder";

function PageHeader({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative min-h-[52vh] bg-pipeco-dark text-white flex items-end overflow-hidden">
      {image && (<><img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/><div className="absolute inset-0 bg-gradient-to-t from-pipeco-dark via-black/60 to-transparent"/></>)}
      <div className="relative container-x px-6 md:px-10 py-24 md:py-32">
        <div className="overline text-emerald-300">{eyebrow}</div>
        <h1 className="headline text-5xl md:text-7xl mt-4 max-w-4xl">{title}</h1>
        {subtitle && <p className="text-white/70 mt-5 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function ProductsList() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data)); }, []);
  return (
    <div data-testid="products-page">
      <PageHeader eyebrow="Our Water Tank Solutions" title="Engineered Water Tanks — Every Technology." image="/tanks/shared-page-hero.webp" subtitle="From GRP sectional and HDG steel to GLS/GFS, Zinc Aluminium, FRP and fully custom engineered solutions."/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(p => (
            <div key={p.id} data-testid={`products-list-card-${p.slug}`} className="bg-white border border-black/5 card-lift group">
              <Link to={`/products/${p.slug}`} className="block aspect-4-5 overflow-hidden bg-slate-100 relative">
                {p.main_image ? (
                  <img src={p.main_image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                ) : (
                  <TankPlaceholder label={p.short_name || p.name} subtitle="Water Storage Solution" tone="dark"/>
                )}
                {p.tank_code && (
                  <div className="absolute top-4 left-4 bg-white/95 text-pipeco-green font-mono text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">{p.tank_code}</div>
                )}
              </Link>
              <div className="p-6">
                <div className="overline text-pipeco-green mb-2">Series {p.tank_code || ""}</div>
                <h3 className="font-display text-2xl text-slate-900 leading-tight">{p.name}</h3>
                <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.intro}</p>
                {p.chips && p.chips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {p.chips.slice(0,3).map(ch => (<span key={ch} className="text-[10px] uppercase tracking-widest text-slate-600 bg-slate-100 px-2 py-1">{ch}</span>))}
                  </div>
                )}
                <div className="flex gap-2 mt-5">
                  <Link to={`/products/${p.slug}`} className="btn-outline-dark flex-1 justify-center text-[10px]" data-testid={`view-product-${p.slug}`}>View Product</Link>
                  <a href={waLink(null, quoteMessage(p.name))} target="_blank" rel="noreferrer" data-testid={`quote-product-${p.slug}`} className="btn-primary flex-1 justify-center text-[10px] !bg-[color:var(--wa-green)] !border-[color:var(--wa-green)]"><MessageCircle size={13}/>Quote</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const { settings } = useSettings();
  useEffect(() => {
    api.get(`/products/${slug}`).then(r => setP(r.data)).catch(()=>setP(null));
    api.get("/projects").then(r => setRelated(r.data.slice(0,3)));
  }, [slug]);
  if (!p) return <div className="section-pad container-x">Loading...</div>;
  const hasImg = !!p.main_image;
  return (
    <div data-testid={`product-detail-${slug}`}>
      <PageHeader eyebrow="Water Storage Solution" title={p.name} subtitle={p.intro} image={hasImg ? p.main_image : "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=2000&q=85"}/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="aspect-16-9 overflow-hidden bg-slate-100 mb-4">
              {hasImg ? <img src={p.main_image} alt={p.name} className="w-full h-full object-cover"/> : <TankPlaceholder label={p.name} subtitle="Water Storage Solution" tone="dark"/>}
            </div>
            {p.gallery && p.gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {p.gallery.map((g,i)=>(<div key={i} className="aspect-1-1 overflow-hidden bg-slate-100"><img src={g} alt={`${p.name} ${i}`} className="w-full h-full object-cover"/></div>))}
              </div>
            )}
            <div className="mt-10 space-y-8">
              {[
                ["Features", p.features],
                ["Applications", p.applications],
                ["Accessories", p.accessories],
              ].map(([label, arr]) => (
                <div key={label}>
                  <div className="overline text-pipeco-green mb-3">{label}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {(arr||[]).map((f,i)=>(<div key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-pipeco-green mt-0.5 shrink-0"/>{f}</div>))}
                  </div>
                </div>
              ))}
              {[
                ["Materials", p.materials],
                ["Available Configurations", p.configurations],
                ["Capacity Information", p.capacity_info],
                ["Technical Information", p.technical_info],
                ["Installation", p.installation],
                ["Quality", p.quality],
                ["Warranty", p.warranty],
              ].map(([label, val]) => (
                val ? (
                  <div key={label}>
                    <div className="overline text-pipeco-green mb-2">{label}</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{val}</p>
                  </div>
                ) : null
              ))}
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-pipeco-dark text-white p-8 sticky top-24">
              <div className="overline text-emerald-300">Request</div>
              <div className="headline text-3xl mt-2">Get a Quote</div>
              <p className="text-white/70 mt-3 text-sm">Talk to a PIPECO project engineer about your {p.name.toLowerCase()} requirements.</p>
              <div className="flex flex-col gap-3 mt-6">
                <a data-testid="product-quote-wa" href={waLink(settings.whatsapp_number, quoteMessage(p.name))} target="_blank" rel="noreferrer" className="btn-whatsapp justify-center"><MessageCircle size={16}/>WhatsApp Quote</a>
                <Link data-testid="product-enquiry" to={`/contact?product=${encodeURIComponent(p.name)}`} className="btn-outline justify-center border-white text-white hover:bg-white hover:text-pipeco-green">Send Enquiry <ArrowRight size={14}/></Link>
                {p.catalogue_url ? (
                  <a data-testid="product-catalogue" href={p.catalogue_url} target="_blank" rel="noreferrer" className="btn-outline justify-center border-emerald-300/60 text-emerald-300 hover:bg-emerald-300 hover:text-pipeco-dark"><Download size={14}/>Download Catalogue (PDF)</a>
                ) : (
                  <div data-testid="product-catalogue-placeholder" className="border border-dashed border-white/25 text-white/60 text-[11px] uppercase tracking-widest px-4 py-3 text-center">
                    <Download size={14} className="inline mr-2 -mt-0.5"/>Catalogue PDF · Coming Soon
                  </div>
                )}
              </div>
              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="overline text-emerald-300 mb-3">Also see</div>
                <div className="space-y-2">
                  {related.map(pr => (
                    <Link key={pr.id} to={`/projects/${pr.id}`} className="flex items-center justify-between text-sm text-white/80 hover:text-white group">
                      <span>{pr.name}</span><ArrowUpRight size={14} className="group-hover:translate-x-1 transition-transform"/>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default ProductsList;
